/**
 * safeNext — validate & sanitize a `?next=` deep-link target for the PM / ops /
 * resident portals. This is the app-portal analogue of the vendor-scoped
 * `safeNext` in `pages/vendor/VendorMagicEnterPage.tsx` (spec 79), and the
 * shared guard used by:
 *   - `ProtectedRoute` (App.tsx) — preserve the attempted destination on the
 *     login redirect (spec 102 §4.6, Wave 0 "returnTo")
 *   - `LoginPage` — land the user on their destination after auth, not `/`
 *   - `api.ts` — preserve the destination on a hard-401 redirect
 *   - the future staff/resident `/enter/:token` landing pages (spec 102 §4.5/§4.8)
 *
 * Guarantees (spec 102 §4.5 / L1):
 *   - internal absolute path only — rejects protocol-relative (`//host`),
 *     schemes (`https:`), and backslash tricks → no open redirect
 *   - never bounces back into an auth / token-enter route (`/enter`, `/login`,
 *     `/logout`, and the `/vendor|/ops|/resident` enter/login variants) → no
 *     redirect loop and no chaining a second token exchange
 *   - strips side-effect query params (spec 94 `?autoAction=`) so a returnTo can
 *     never silently auto-fire an action on arrival
 *
 * Returns the sanitized internal path, or `null` when the input is unsafe.
 */
export function safeNext(next: string | null | undefined): string | null {
  if (!next || typeof next !== 'string') return null;
  // Absolute internal path only. Reject protocol-relative (`//host`).
  if (!next.startsWith('/') || next.startsWith('//')) return null;

  // Split off fragment then query.
  const noHash = next.split('#')[0];
  const qIdx = noHash.indexOf('?');
  const path = qIdx === -1 ? noHash : noHash.slice(0, qIdx);
  const rawQuery = qIdx === -1 ? '' : noHash.slice(qIdx + 1);

  // A backslash in the PATH can be browser-normalized to `/` → protocol-relative
  // open redirect, so reject it. A `\` or `://` inside the QUERY is just data
  // (e.g. `?ref=https://x`) and is preserved (review LOW-5).
  if (path.includes('\\') || path.includes(':')) return null;

  // Never target an auth / token-enter route (loop + token-chaining guard).
  // Case-insensitive — React Router v6 matches routes case-insensitively, so
  // `/Enter/<token>` must be blocked too (review LOW-3).
  if (/^\/(enter|login|logout)(\/|$)/i.test(path)) return null;
  if (/^\/(vendor|ops|resident)\/(enter|login)(\/|$)/i.test(path)) return null;

  if (!rawQuery) return path;

  // Strip side-effect params (e.g. spec-94 auto-run) from the preserved query.
  const params = new URLSearchParams(rawQuery);
  params.delete('autoAction');
  const q = params.toString();
  return q ? `${path}?${q}` : path;
}
